import * as stylex from "@stylexjs/stylex";
import { Clock, Sunset } from "lucide-react";
import { copy } from "@/copy";
import { type DayRibbon, RIBBON_END, RIBBON_START, type RibbonLabel } from "@/domain/dayRibbon";
import { type Clock as ClockSetting, formatClock, formatDuration, formatHour, minutesOfDay } from "@/domain/time";
import type { TodayHero } from "@/domain/todayHero";
import "@/theme/tokens.stylex";
import { colors, radii } from "@/theme/tokens.stylex";
import "@/theme/type.stylex";
import { type } from "@/theme/type.stylex";
import { Eyebrow } from "@/ui/Eyebrow";
import { Icon } from "@/ui/Icon";
type OpenDayProps = {
  hero: Extract<TodayHero<never>, {
    kind: "none" | "ended";
  }>;
  ribbon: DayRibbon;
  now: Date;
  clock: ClockSetting;
  tz: string;
};
const MARKER = 32;
const SUNSET_ABOVE = -3;
const SUNSET_BELOW = 54;
const LABEL_SHIFT: Record<RibbonLabel, string> = {
  start: "translateX(0)",
  center: "translateX(-50%)",
  end: "translateX(-100%)"
};
const mono = {
  fontFamily: type.fontMono,
  letterSpacing: "0.54px",
  textTransform: "uppercase",
  lineHeight: 1
} as const;
const _temp = {
  kVAEAm: "x10l6tqk",
  kYYq5F: "x10no89f",
  kLqNvP: "x1o0tod",
  kWkggS: "xfenp3h",
  "$$css": true
};
const _temp2 = {
  kVAEAm: "x10l6tqk",
  k87sOh: "xndqk7f",
  krVfgx: "xbfrwjf",
  kaIpWk: "xqhrb7l",
  kWkggS: "xh540kj",
  "$$css": true
};
const _temp3 = {
  kMv6JI: "xr0iwhn",
  kb6lSQ: "xbzybt3",
  kP9fke: "xtvhhri",
  kLWn49: "xo5v014",
  kVAEAm: "x10l6tqk",
  k87sOh: "x13vifvy",
  kGuDYH: "xif65rj",
  khDVqt: "xuxw1ft",
  "$$css": true
};
const _temp4 = {
  kVAEAm: "x10l6tqk",
  k3aq6I: "xuuh30",
  k1xSpc: "x78zum5",
  "$$css": true
};
const _temp5 = {
  kVAEAm: "x10l6tqk",
  k87sOh: "x8l1umf",
  kzqmXN: "xfo62xy",
  kZKoxP: "xd7y6wv",
  keTefX: "x1hb08if",
  kWkggS: "xfenp3h",
  "$$css": true
};
const _temp6 = {
  kVAEAm: "x10l6tqk",
  k87sOh: "xys9btg",
  kzqmXN: "x1td3qas",
  kZKoxP: "x10w6t97",
  keTefX: "x162z183",
  kaIpWk: "xqhrb7l",
  kWkggS: "xfenp3h",
  kMwMTN: "xnnvkky",
  kGVxlE: "x1homwli",
  k1xSpc: "x78zum5",
  kGNEyG: "x6s0dn4",
  kjj79g: "xl56j7k",
  "$$css": true
};
const styles = {
  elapsed: (width: string) => [_temp, {
    kzqmXN: width != null ? "x5lhr3w" : width,
    $$css: true
  }, {
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(width)
  }],
  ended: (left: string, width: string) => [_temp2, {
    kLqNvP: left != null ? "x1lxsm33" : left,
    kzqmXN: width != null ? "x5lhr3w" : width,
    $$css: true
  }, {
    "--x-insetInlineStart": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(left),
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(width)
  }],
  nowLabel: (left: string, transform: string) => [_temp3, {
    kLqNvP: left != null ? "x1lxsm33" : left,
    k3aq6I: transform != null ? "xsqj5wx" : transform,
    $$css: true
  }, {
    "--x-insetInlineStart": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(left),
    "--x-transform": transform != null ? transform : undefined
  }],
  sunsetIcon: (left: string, top: number) => [_temp4, {
    k87sOh: top != null ? "xjbys53" : top,
    kLqNvP: left != null ? "x1lxsm33" : left,
    $$css: true
  }, {
    "--x-top": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(top),
    "--x-insetInlineStart": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(left)
  }],
  sunsetTick: (left: string) => [_temp5, {
    kLqNvP: left != null ? "x1lxsm33" : left,
    $$css: true
  }, {
    "--x-insetInlineStart": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(left)
  }],
  marker: (left: string) => [_temp6, {
    kLqNvP: left != null ? "x1lxsm33" : left,
    $$css: true
  }, {
    "--x-insetInlineStart": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(left)
  }]
};
export function OpenDay({
  hero,
  ribbon,
  now,
  clock,
  tz
}: OpenDayProps) {
  const until = formatHour(RIBBON_END, clock);
  const wrapped = hero.kind === "ended";
  return <div className="x78zum5 xdt5ytf x1c1vhfx xeuugli">
      <div className="x78zum5 xdt5ytf x1v2ro7d">
        <Eyebrow>{copy.today.open.eyebrow}</Eyebrow>
        <span className="xdjib4s x1v94iar x1f41ap9 xipi2l9 x1fzhlzt x104kibb x1h7i4cw x1ua5tub xb3r6kr">
          {wrapped ? copy.today.open.wrapTitle : copy.today.open.wideTitle}
        </span>
        <span className="x12feioo xbt1u0e xwn7fz2 x1fzhlzt">
          {wrapped ? copy.today.open.wrapBody(hero.done, until) : copy.today.open.wideBody(until, leftText(ribbon.left))}
        </span>
      </div>
      <Ribbon ribbon={ribbon} now={now} clock={clock} tz={tz} />
    </div>;
}
function Ribbon({
  ribbon,
  now,
  clock,
  tz
}: Pick<OpenDayProps, "ribbon" | "now" | "clock" | "tz">) {
  const elapsed = percentText(ribbon.elapsed);
  return <div aria-hidden="true" className="x1n2onr6 x1a6kali">
      <span {...stylex.props(styles.nowLabel(elapsed, LABEL_SHIFT[ribbon.label]))}>
        {copy.today.open.now(formatClock(minutesOfDay(now, tz), clock))}
      </span>
      {ribbon.sunset !== undefined && <>
          <span {...stylex.props(styles.sunsetIcon(percentText(ribbon.sunset.at), ribbon.sunset.below ? SUNSET_BELOW : SUNSET_ABOVE))}>
            <Icon icon={Sunset} size={18} />
          </span>
          <div {...stylex.props(styles.sunsetTick(percentText(ribbon.sunset.at)))} />
        </>}
      <div className="x1n2onr6 x1v9usgg xqhrb7l xh540kj xb3r6kr">
        <div {...stylex.props(styles.elapsed(elapsed))} />
        {ribbon.ended.map(({
        start,
        width
      }) => <div key={start} {...stylex.props(styles.ended(percentText(start), percentText(width)))} />)}
      </div>
      <div {...stylex.props(styles.marker(elapsed))}>
        <Icon icon={Clock} size={20} strokeWidth={2} />
      </div>
      <span className="xr0iwhn xbzybt3 xtvhhri xo5v014 x10l6tqk x1ey2m1c x4z9k3i x1o0tod">{formatHour(RIBBON_START, clock)}</span>
      <span className="xr0iwhn xbzybt3 xtvhhri xo5v014 x10l6tqk x1ey2m1c x4z9k3i xtijo5x">{formatHour(RIBBON_END, clock)}</span>
    </div>;
}
function leftText(minutes: number): string | undefined {
  return minutes > 0 ? formatDuration(minutes) : undefined;
}
function percentText(value: number): string {
  return `${value}%`;
}